"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.contraintsKeyArgumentsCallback = void 0;
const LoginFieldPolicy = {
    merge(existing, incoming) {
        /**
         * LIA-70677 Bypass updating the login for Anonymous users. This can happen when the current
         * user is anonymous and there is a query result for another anonymous user that has a different login.
         * We avoid updating the cache to prevent re-renders that can inadvertently cause modals/overlays to close.
         * Also bypass overriding an existing login when the login is just an empty string.
         *
         * In the future we might consider using different IDs for the principal anon user and guest users
         * that are allowed to post pseudo-anonymously
         */
        if (existing && (existing === 'Anonymous' || incoming === '')) {
            return existing;
        }
        return incoming;
    }
};
/**
 * A custom keyArgs function used to customize the cache key for a field.
 *
 * If the variables object has constraints, we sort and serialize the contraints to avoid cache duplications
 * since the same field may be queried with different constraints.
 *
 * If not, we use the field name.
 *
 * See https://www.apollographql.com/docs/react/caching/cache-field-behavior/#providing-a-keyargs-function
 */
const contraintsKeyArgumentsCallback = (_, { fieldName, variables }) => {
    var _a;
    return Object.keys((_a = variables === null || variables === void 0 ? void 0 : variables.constraints) !== null && _a !== void 0 ? _a : {}).length > 0
        ? `${fieldName}:${JSON.stringify(Object.fromEntries(Object.entries(variables.constraints).sort()))}`
        : fieldName;
};
exports.contraintsKeyArgumentsCallback = contraintsKeyArgumentsCallback;
const KudosCountFieldPolicy = {
    keyArgs: exports.contraintsKeyArgumentsCallback,
    merge(existing, incoming) {
        /**
         * LIA-79822 Bypass incoming value for Kudos count received/given if it is null.
         */
        if (existing && incoming === null) {
            return existing;
        }
        return incoming;
    }
};
const TopicsCountPolicy = {
    merge(existing, incoming) {
        /**
         * LIA-79806 Bypass incoming value for topics count if it is null.
         */
        if (existing && incoming === null) {
            return existing;
        }
        return incoming;
    }
};
const CommunityTypePolicy = {
    fields: {
        profileSettings: {
            merge: true
        },
        ssoSettings: {
            merge: true
        },
        communityPolicies: {
            merge: true
        },
        userManagementProperties: {
            merge: true
        }
    }
};
const UserTypePolicy = {
    fields: {
        login: LoginFieldPolicy,
        profileSettings: {
            merge: true
        },
        registrationData: {
            merge: true
        },
        userPolicies: {
            merge: true
        },
        userProperties: {
            merge: true
        },
        userSettings: {
            merge: true
        },
        kudosGivenCount: KudosCountFieldPolicy,
        kudosReceivedCount: KudosCountFieldPolicy,
        messagesCount: {
            keyArgs: exports.contraintsKeyArgumentsCallback
        },
        solutionsCount: {
            keyArgs: exports.contraintsKeyArgumentsCallback
        },
        topicsCount: {
            keyArgs: exports.contraintsKeyArgumentsCallback
        },
        repliesSettings: {
            merge: true
        }
    }
};
const SolutionMessageFieldPolicy = {
    keyArgs: ['id', 'solution']
};
const SolutionDataTypePolicy = {
    keyFields: ['message'],
    fields: {
        message: SolutionMessageFieldPolicy
    }
};
const AttachmentDataTypePolicy = {
    keyFields: ['id', 'url']
};
const NodeTypePolicy = {
    fields: {
        ancestors: {
            keyArgs: false,
            merge: true
        },
        children: {
            merge: true
        },
        cwaSettings: {
            merge: true
        },
        cwaProperties: {
            merge: true
        },
        members: {
            keyArgs: false,
            merge: true
        },
        membershipPolicies: {
            merge: true
        },
        userContext: {
            merge: true
        },
        categoryPolicies: {
            merge: true
        },
        grouphubPolicies: {
            merge: true
        },
        boardPolicies: {
            merge: true
        },
        blogPolicies: {
            merge: true
        },
        tkbPolicies: {
            merge: true
        },
        forumPolicies: {
            merge: true
        },
        ideaPolicies: {
            merge: true
        },
        occasionPolicies: {
            merge: true
        },
        topicsCount: TopicsCountPolicy
    }
};
const RevisionMergeTypePolicy = {
    merge(existing, incoming) {
        if (existing && incoming === null) {
            return existing;
        }
        return incoming;
    }
};
const CoverImageMergeTypePolicy = {
    merge(existing, incoming, { mergeObjects }) {
        if (existing && incoming === null) {
            return existing;
        }
        return mergeObjects(existing, incoming);
    }
};
const AuthorMergeTypePolicy = {
    merge: (existing, incoming, { mergeObjects }) => {
        if (!existing && !!incoming) {
            return incoming;
        }
        else if (!!existing && !incoming) {
            return existing;
        }
        return mergeObjects(existing, incoming);
    }
};
const ParentMergeTypePolicy = {
    merge: (existing, incoming, { mergeObjects }) => {
        if (!existing && !!incoming) {
            return incoming;
        }
        else if (!!existing && !incoming) {
            return existing;
        }
        return mergeObjects(existing, incoming);
    }
};
const RepliesFieldPolicy = {
    keyArgs: false,
    merge(existing, incoming, { args, readField, mergeObjects }) {
        var _a, _b;
        const existingEdges = existing
            ? (_a = readField('edges', existing)) !== null && _a !== void 0 ? _a : []
            : [];
        const incomingEdges = (_b = readField('edges', incoming)) !== null && _b !== void 0 ? _b : [];
        const seen = new Set();
        const resultEdges = [];
        function addEdge(edge) {
            if (!edge) {
                return;
            }
            const nodeRef = readField('node', edge);
            const nodeId = nodeRef ? readField('id', nodeRef) : undefined;
            const cursor = readField('cursor', edge);
            const key = nodeId !== null && nodeId !== void 0 ? nodeId : (cursor ? `cursor:${cursor}` : undefined);
            if (key && !seen.has(key)) {
                seen.add(key);
                resultEdges.push(edge);
            }
        }
        let isPrepend = false;
        let isAppend = false;
        if (args && typeof args === 'object') {
            const a = args;
            isPrepend = Object.prototype.hasOwnProperty.call(a, 'before') && a.before != null;
            isAppend = Object.prototype.hasOwnProperty.call(a, 'after') && a.after != null;
        }
        if (isPrepend) {
            incomingEdges.forEach(edge => {
                addEdge(edge);
            });
            existingEdges.forEach(edge => {
                addEdge(edge);
            });
        }
        else if (isAppend) {
            existingEdges.forEach(edge => {
                addEdge(edge);
            });
            incomingEdges.forEach(edge => {
                addEdge(edge);
            });
        }
        else {
            existingEdges.forEach(edge => {
                addEdge(edge);
            });
            incomingEdges.forEach(edge => {
                addEdge(edge);
            });
        }
        const existingPageInfoObject = existing === null || existing === void 0 ? void 0 : existing.pageInfo;
        const incomingPageInfoObject = incoming === null || incoming === void 0 ? void 0 : incoming.pageInfo;
        const mergedPageInfo = mergeObjects(existingPageInfoObject, incomingPageInfoObject);
        const incomingObject = incoming && typeof incoming === 'object' ? incoming : {};
        return Object.assign(Object.assign({}, incomingObject), { edges: resultEdges, pageInfo: mergedPageInfo });
    }
};
const MessageTypePolicy = {
    fields: {
        attachments: {
            keyArgs: false,
            merge: true
        },
        board: {
            merge: true
        },
        author: AuthorMergeTypePolicy,
        contentWorkflow: {
            merge: true
        },
        coverImage: CoverImageMergeTypePolicy,
        replies: RepliesFieldPolicy,
        descendants: {
            keyArgs: false,
            merge: true
        },
        tags: {
            keyArgs: false,
            merge: true
        },
        messagePolicies: {
            merge: true
        },
        blogMessagePolicies: {
            merge: true
        },
        tkbMessagePolicies: {
            merge: true
        },
        revisions: RevisionMergeTypePolicy,
        parent: ParentMergeTypePolicy,
        occasionData: {
            merge: true
        }
    },
    merge(existing, incoming, { mergeObjects }) {
        // Keep newer revision; otherwise, preserve existing fields via merge to allow field policies to run
        if ((existing === null || existing === void 0 ? void 0 : existing.revisionNum) != null &&
            (incoming === null || incoming === void 0 ? void 0 : incoming.revisionNum) != null &&
            incoming.revisionNum < existing.revisionNum) {
            return existing;
        }
        if (!existing && incoming) {
            return incoming;
        }
        if (existing && !incoming) {
            return existing;
        }
        return mergeObjects(existing, incoming);
    }
};
const RevisionTypePolicy = {
    fields: {
        message: {
            merge: true
        }
    }
};
// Ensure the RepliesFieldPolicy applies to concrete forum message types as well.
const buildForumMessageTypePolicy = () => ({
    fields: {
        replies: RepliesFieldPolicy
    },
    merge(existing, incoming, { mergeObjects }) {
        if ((existing === null || existing === void 0 ? void 0 : existing.revisionNum) != null &&
            (incoming === null || incoming === void 0 ? void 0 : incoming.revisionNum) != null &&
            incoming.revisionNum < existing.revisionNum) {
            return existing;
        }
        if (!existing && incoming) {
            return incoming;
        }
        if (existing && !incoming) {
            return existing;
        }
        return mergeObjects(existing, incoming);
    }
});
const ForumTopicMessageTypePolicy = buildForumMessageTypePolicy();
const ForumReplyMessageTypePolicy = buildForumMessageTypePolicy();
const PrivateConversationTypePolicy = {
    keyFields: ['id', 'latestNote', ['id']]
};
const ImageTypePolicy = {
    keyFields: ['url']
};
const PrivateConversationConnectionTypePolicy = {
    merge: (
    // eslint-disable-next-line @typescript-eslint/default-param-last
    existing = {
        pageInfo: undefined,
        totalCount: 0,
        edges: undefined
    }, incoming, { readField, mergeObjects }) => {
        // Always remove duplicated edges from incoming and merge
        // This is to bypass a list error that duplicates the last page when selecting
        // a private conversation while it's loading more pages
        const incomingEdges = readField('edges', incoming);
        const filteredEdges = incomingEdges.filter((value, index) => {
            const stringValue = JSON.stringify(value);
            return (index ===
                incomingEdges.findIndex(conversation => {
                    return JSON.stringify(conversation) === stringValue;
                }));
        });
        const filteredIncoming = Object.assign(Object.assign({}, incoming), { edges: filteredEdges });
        return mergeObjects(existing, filteredIncoming);
    }
};
const quiltItemsPolicy = {
    merge: true,
    fields: {
        items: {
            merge(existing, incoming) {
                return incoming;
            }
        }
    }
};
const formPolicies = {
    FormResponse: {
        keyFields: false
    },
    FormResource: {
        keyFields: false
    },
    Form: {
        keyFields: false
    },
    FormField: {
        keyFields: false
    },
    FormFieldRef: {
        keyFields: false
    },
    FormLayout: {
        keyFields: false
    }
};
const quiltPolicies = {
    QuiltResource: {
        keyFields: ['lastUpdatedTime', 'quilt', ['id']]
    },
    QuiltContainer: quiltItemsPolicy
};
const quickMerge = { merge: true };
const ThemeTypePolicies = {
    __typename: quickMerge,
    id: quickMerge,
    displayName: quickMerge,
    localOverride: quickMerge,
    animation: quickMerge,
    avatar: quickMerge,
    basics: quickMerge,
    border: quickMerge,
    boxShadow: quickMerge,
    buttons: quickMerge,
    cards: quickMerge,
    chip: quickMerge,
    colorLightness: quickMerge,
    colors: quickMerge,
    coreTypes: quickMerge,
    divider: quickMerge,
    dropdown: quickMerge,
    email: quickMerge,
    emoji: quickMerge,
    heading: quickMerge,
    icons: quickMerge,
    imagePreview: quickMerge,
    input: quickMerge,
    link: quickMerge,
    listGroup: quickMerge,
    loading: quickMerge,
    modal: quickMerge,
    navbar: quickMerge,
    pager: quickMerge,
    panel: quickMerge,
    popover: quickMerge,
    prism: quickMerge,
    rte: quickMerge,
    unstyledListItem: quickMerge,
    tags: quickMerge,
    toasts: quickMerge,
    typography: quickMerge,
    yiq: quickMerge
};
const ThemeTypePolicy = {
    fields: ThemeTypePolicies
};
const PermissionValueTypePolicy = {
    merge: true,
    fields: {
        inheritedFrom: {
            merge: true
        }
    }
};
// messageSearch and quickMessageSearch do not contain an id so require a custom merge function
const SearchResultsTypePolicy = {
    fields: {
        results: {
            merge: true
        }
    }
};
/**
 * Ensures that search results receive unique cache entries by creating a Cache ID from the combined id and snippet.
 * If this is not in place, then there is collision for the result snippet, which can manifest as the incorrect substring
 * becoming highlighted in the UI
 */
const SearchResultTypePolicy = {
    keyFields: ['id', 'snippet']
};
const TopLevelMessageSearchResultTypePolicy = {
    keyFields: ['id', 'snippet', 'descendants', ['edges', ['node', ['id']]]]
};
const OpenIdConnectSelfServiceIdpTypePolicy = {
    keyFields: ['provider', ['providerId']]
};
/*
 * ComponentTypePolicy is used to merge the component data from the cache.
 */
const ComponentTypePolicy = {
    merge: (existing, incoming, { mergeObjects }) => {
        return mergeObjects(existing, incoming);
    }
};
/**
 * LIA-85210 LIA-87965 When the "disable anon access" feature is enabled then only properties marked with
 * the @anonAllowed are allowed to be queried by anon users. In some cases, the are objects that contain
 * a mix of fields that are anon allowed and not anon allowed. For a given query for an object, if one
 * or more of the fields is not allowed, then the entire object will return null. This can result in a
 * query returning null for an object and wiping out previous queries for that object that only contained
 * anon allowed fields. This mostly effects the community object that contains many fields some of which
 * are anon allowed.
 *
 * TODO: Fix the underlying issue where the server returns null for an object when some fields are
 * anonAllowed and others are not. Instead we should return all the valid fields and null for the
 * invalid fields.
 */
const QueryTypePolicy = {
    fields: {
        community: {
            merge(existing, incoming, { mergeObjects }) {
                if (existing && incoming === null) {
                    return existing;
                }
                return mergeObjects(existing, incoming);
            }
        },
        permissionsForCoreNode: {
            keyArgs: ['id']
        }
    }
};
const TagPolicy = {
    keyFields: false
};
const WorkstreamConnectionTypePolicy = {
    merge: (
    // eslint-disable-next-line @typescript-eslint/default-param-last
    existing = {
        pageInfo: undefined,
        totalCount: 0,
        edges: undefined
    }, incoming, { readField, mergeObjects }) => {
        // Always remove duplicated edges from incoming and merge
        // This is to bypass a list error that duplicates the last page when selecting
        // a workstream while it's loading more pages
        const incomingEdges = readField('edges', incoming);
        const filteredEdges = incomingEdges.filter((value, index) => {
            const stringValue = JSON.stringify(value);
            return (index ===
                incomingEdges.findIndex(workstream => {
                    return JSON.stringify(workstream) === stringValue;
                }));
        });
        const filteredIncoming = Object.assign(Object.assign({}, incoming), { edges: filteredEdges });
        return mergeObjects(existing, filteredIncoming);
    }
};
/**
 * Builds the type policies, allows for a callback to load themes from the filesystem for Admin and local dev
 * of Enduser.
 */
function buildTypePolicies() {
    return Object.assign(Object.assign(Object.assign(Object.assign({ AssociatedImage: ImageTypePolicy, Component: ComponentTypePolicy, Attachment: AttachmentDataTypePolicy, Board: NodeTypePolicy, Category: NodeTypePolicy, Community: CommunityTypePolicy, CookieProperties: {
            merge: true
        }, FeaturedContentWidget: {
            merge: true
        } }, formPolicies), { GroupHub: NodeTypePolicy, Message: MessageTypePolicy, ForumTopicMessage: ForumTopicMessageTypePolicy, ForumReplyMessage: ForumReplyMessageTypePolicy, Node: NodeTypePolicy, PermissionValue: PermissionValueTypePolicy, PrivateConversation: PrivateConversationTypePolicy, PrivateConversationConnection: PrivateConversationConnectionTypePolicy, QuickMessageSearchResults: SearchResultsTypePolicy, MessageSearchResults: SearchResultsTypePolicy, NodeSearchResult: SearchResultTypePolicy, QuickMessageSearchResult: SearchResultTypePolicy, TopLevelMessageSearchResult: TopLevelMessageSearchResultTypePolicy, UserSearchResult: SearchResultTypePolicy, DescendantMessageSearchResult: SearchResultTypePolicy }), quiltPolicies), { Revision: RevisionTypePolicy, SolutionData: SolutionDataTypePolicy, Theme: ThemeTypePolicy, User: UserTypePolicy, Query: QueryTypePolicy, OpenIdConnectSelfServiceIdp: OpenIdConnectSelfServiceIdpTypePolicy, Tag: TagPolicy, WorkstreamConnection: WorkstreamConnectionTypePolicy, TriggerDetail: { merge: true } });
}
exports.default = buildTypePolicies;
//# sourceMappingURL=typePolicies.js.map